import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

export interface DataTableFilterChip {
    /** The column id. */
    id: string;
    /** The column header as plain text. */
    label: string;
    /** The plain-text description of the filter, for example "greater than 10,000". */
    description: string;
}

interface DataTableFilterBarProps {
    chips: readonly DataTableFilterChip[];
    /** The rows that pass every filter. */
    shown: number;
    /** The rows before any filter. */
    total: number;
    /** Selected rows that the filters hide. */
    hiddenSelected: number;
    onOpen: (id: string) => void;
    onRemove: (id: string) => void;
    onClear: () => void;
}

/** The strip above the table that lists the active column filters. */
export default function DataTableFilterBar({
    chips,
    shown,
    total,
    hiddenSelected,
    onOpen,
    onRemove,
    onClear,
}: DataTableFilterBarProps) {
    return (
        <div
            className={cn(
                'flex h-7 items-center gap-1.5 overflow-x-auto scrollbar-none px-1.5',
                'border-b border-[rgb(var(--color-divider))]',
            )}
        >
            {chips.map((chip) => (
                <span
                    key={chip.id}
                    className={cn(
                        'inline-flex h-5 shrink-0 items-center rounded-[4px] border text-annotation whitespace-nowrap',
                        'border-[rgb(var(--color-accent))] bg-[rgb(var(--color-accent)/0.15)]',
                        'text-[rgb(var(--color-text-primary))]',
                    )}
                >
                    <button
                        type="button"
                        onClick={() => onOpen(chip.id)}
                        className="h-full rounded-l-[3px] pl-2 pr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]"
                    >
                        {`${chip.label}: ${chip.description}`}
                    </button>
                    <button
                        type="button"
                        aria-label={`Remove filter ${chip.label}`}
                        onClick={() => onRemove(chip.id)}
                        className="grid h-full w-5 place-items-center rounded-r-[3px] hover:bg-[rgb(var(--color-accent)/0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            ))}
            <span className="ml-auto flex shrink-0 items-center gap-2 pl-2">
                {hiddenSelected > 0 && (
                    <span className="text-caption text-[rgb(var(--color-text-tertiary))]">
                        {`${hiddenSelected.toLocaleString('en-GB')} selected ${
                            hiddenSelected === 1 ? 'row is' : 'rows are'
                        } hidden by filters`}
                    </span>
                )}
                <span className="font-mono tabular-nums text-caption text-[rgb(var(--color-text-secondary))]">
                    {`${shown.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} rows`}
                </span>
                <button
                    type="button"
                    onClick={onClear}
                    className="text-annotation text-[rgb(var(--color-accent))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)] rounded"
                >
                    Clear filters
                </button>
            </span>
        </div>
    );
}
